import React, { useEffect, useState } from 'react';
import { Card, Table, Form, Input, InputNumber, DatePicker, Button, Row, Col, Space, message, Select, Dropdown } from 'antd';
import * as XLSX from 'xlsx';
import axios from 'axios';
import dayjs from 'dayjs';
// 1. 引入港口接口和工具函数
import { useGetStartPortQuery, useGetEndPortQuery } from '@/store/apiSlice';
import { createJsonOptions } from '@/scripts/utils';

const { RangePicker } = DatePicker;

const columns = [
    { title: '起始港', dataIndex: 'originlocation', key: 'originlocation', align: 'center' },
    { title: '目的港', dataIndex: 'destinationlocation', key: 'destinationlocation', align: 'center' },
    { title: '开航时间', dataIndex: 'earlytime', key: 'earlytime', align: 'center' },
    { title: '到港时间', dataIndex: 'endtime', key: 'endtime', align: 'center' },
    { title: '箱型', dataIndex: 'box_type', key: 'box_type', align: 'center' },
    { title: '箱量', dataIndex: 'container_quantity', key: 'container_quantity', align: 'center' },
    { title: '重量', dataIndex: 'weight', key: 'weight', align: 'center' },
    { title: '品名', dataIndex: 'cargo_description', key: 'cargo_description', align: 'center' },
    {
        title: '下单时间',
        dataIndex: 'book_time',
        key: 'book_time',
        align: 'center',
        render: (text) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    { title: '账号', dataIndex: 'username', key: 'username', align: 'center' },
    { title: 'bookingnumber', dataIndex: 'bookingnumber', key: 'bookingnumber', align: 'center' },
    { title: '约号', dataIndex: 'quotation_number', key: 'quotation_number', align: 'center' },
    { title: '运价', dataIndex: 'price', key: 'price', align: 'center' },
    { title: '总价', dataIndex: 'total_price', key: 'total_price', align: 'center' },
];

export default function OrderListPage() {
    const [form] = Form.useForm();
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    // 2. 获取港口下拉选项
    const { data: startPortOptions } = useGetStartPortQuery(1);
    const { data: endPortOptions } = useGetEndPortQuery(1);

    //分页状态
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    // 记录当前的搜索参数
    const [searchParams, setSearchParams] = useState({});

    // page 页码（从 1 开始），per_page 每页条数
    const fetchList = (page, per_page, params = {}) => {
        setLoading(true);
        axios
            .get('http://183.249.91.105:15600/hpl/orders', {
                params: { page, per_page, ...params },
            })
            .then((res) => {
                setData(res.data.data || []);
                setTotal(res.data.total || 0);
            })
            .catch((err) => {
                console.error(err);
                message.error('获取订单列表失败');
            })
            .finally(() => setLoading(false));
    };

    // 首次进入加载第一页、每页 10 条
    useEffect(() => {
        fetchList(1, 10, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 点击搜索
    const handleSearch = (values) => {
        const params = { ...values };

        // 处理离港日期范围
        if (values.departure_date && values.departure_date.length === 2) {
            params.departure_date_start = values.departure_date[0].format('YYYY-MM-DD');
            params.departure_date_end = values.departure_date[1].format('YYYY-MM-DD');
        }
        delete params.departure_date;

        // 处理订舱时间范围
        if (values.book_time && values.book_time.length === 2) {
            params.book_time_start = values.book_time[0].format('YYYY-MM-DDTHH:mm:ss');
            params.book_time_end = values.book_time[1].format('YYYY-MM-DDTHH:mm:ss');
        }
        delete params.book_time;

        // 过滤掉空值参数
        Object.keys(params).forEach((key) => {
            if (params[key] === undefined || params[key] === null || params[key] === '') {
                delete params[key];
            }
        });

        setSearchParams(params);
        setPagination({ ...pagination, current: 1 }); // 搜索时重置到第一页
        fetchList(1, pagination.pageSize, params);
    };

    // 点击重置
    const handleReset = () => {
        form.resetFields();
        setSearchParams({});
        setPagination({ ...pagination, current: 1 });
        fetchList(1, pagination.pageSize, {});
    };

    // 翻页 / 切换每页条数
    const handleTableChange = (page, pageSize) => {
        setPagination({ current: page, pageSize });
        fetchList(page, pageSize, searchParams);
    };

    // 导出
    const exportToExcel = (rows, fileName) => {
        // 导出列中文表头 / 对应字段
        const exportColumns = [
            { title: '起始港', dataIndex: 'originlocation' },
            { title: '目的港', dataIndex: 'destinationlocation' },
            { title: '开航时间', dataIndex: 'earlytime' },
            { title: '到港时间', dataIndex: 'endtime' },
            { title: '箱型', dataIndex: 'box_type' },
            { title: '箱量', dataIndex: 'container_quantity' },
            { title: '重量', dataIndex: 'weight' },
            { title: '品名', dataIndex: 'cargo_description' },
            { title: '下单时间', dataIndex: 'book_time' },
            { title: '账号', dataIndex: 'username' },
            { title: 'bookingnumber', dataIndex: 'bookingnumber' },
            { title: '约号', dataIndex: 'quotation_number' },
            { title: '运价', dataIndex: 'price' },
            { title: '总价', dataIndex: 'total_price' },
        ];

        if (!rows || rows.length === 0) {
            message.warning('暂无可导出的数据');
            return;
        }

        const header = exportColumns.map((c) => c.title);
        const body = rows.map((row) =>
            exportColumns.map((c) => {
                const val = row[c.dataIndex];
                // 时间格式化
                if (c.dataIndex === 'book_time' && val) {
                    return dayjs(val).format('YYYY-MM-DD HH:mm:ss');
                }
                return val ?? '';
            }),
        );

        const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
        ws['!cols'] = header.map(() => ({ wch: 20 })); // 列宽
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '订单列表');
        XLSX.writeFile(wb, fileName);
    };

    // 导出当前页
    const handleExportCurrent = () => {
        if (!data || data.length === 0) {
            message.warning('当前页暂无数据');
            return;
        }
        exportToExcel(data, `HPL QQ 订单列表_当前页_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`);
    };

    // 导出全部页
    const handleExportAll = async () => {
        const hide = message.loading('正在导出全部数据...', 0);
        const pageSize = 1000;
        try {
            const allRows = [];
            let page = 1;
            let totalPages = 1;

            // 先拉第一页拿 total
            const firstRes = await axios.get('http://183.249.91.105:15600/hpl/orders', {
                params: { page: 1, per_page: pageSize, ...searchParams },
            });
            allRows.push(...(firstRes.data.data || []));
            const totalCount = firstRes.data.total || 0;
            totalPages = Math.ceil(totalCount / pageSize);

            // 剩余页逐页拉取
            for (let p = 2; p <= totalPages; p++) {
                const res = await axios.get('http://183.249.91.105:15600/hpl/orders', {
                    params: { page: p, per_page: pageSize, ...searchParams },
                });
                allRows.push(...(res.data.data || []));
            }

            exportToExcel(allRows, `HPL QQ 订单列表_全部_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`);
        } catch (err) {
            console.error(err);
            message.error('导出失败，请稍后重试');
        } finally {
            hide();
        }
    };

    return (
        <Card style={{ marginTop: 2 }}>
            <Form form={form} onFinish={handleSearch} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                <Row gutter={16}>
                    <Col span={6}>
                        {/* 3. Input 换成 Select */}
                        <Form.Item name="origin" label="起始港">
                            <Select options={createJsonOptions(startPortOptions)} showSearch allowClear placeholder="请选择起始港" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="destination" label="目的港">
                            <Select options={createJsonOptions(endPortOptions)} showSearch allowClear placeholder="请选择目的港" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="container_quantity" label="箱量">
                            <InputNumber style={{ width: '100%' }} placeholder="请输入箱量" min={1} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="bookingnumber" label="bookingnumber" labelCol={{ span: 10 }}>
                            <Input placeholder="请输入 Bookingnumber" allowClear />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="quotation_number" label="约号">
                            <Input placeholder="请输入约号" allowClear />
                        </Form.Item>
                    </Col>
                    <Col span={9}>
                        <Form.Item name="departure_date" label="离港日期">
                            <RangePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={9}>
                        <Form.Item name="book_time" label="订舱时间">
                            <RangePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row justify="end" gutter={16} style={{ marginBottom: 16 }}>
                    <Col>
                        <Space>
                            <Button onClick={handleReset}>重置</Button>
                            <Button type="primary" htmlType="submit">
                                搜索
                            </Button>
                            <Dropdown
                                trigger={['hover']}
                                menu={{
                                    items: [
                                        { key: 'current', label: '导出当前页' },
                                        { key: 'all', label: '导出全部页' },
                                    ],
                                    onClick: ({ key }) => {
                                        if (key === 'current') handleExportCurrent();
                                        if (key === 'all') handleExportAll();
                                    },
                                }}
                            >
                                <Button>导出</Button>
                            </Dropdown>
                        </Space>
                    </Col>
                </Row>
            </Form>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={data}
                loading={loading}
                bordered
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (t) => `共 ${t} 条`,
                    onChange: handleTableChange,
                }}
            />
        </Card>
    );
}
